import { ReactElement, useState } from 'react';
import styled from 'styled-components';
import {
  ContainerContent,
  MfaBackground,
  MfaLayout,
  MfaHeader,
  MfaContent,
  Container,
  ContainerHeader,
  ContainerHeading,
  StyledButton,
  StyledLink,
  SignOutButton
} from '../styles';
import { UniLogo } from '../../../atoms/uni-logo';
import { MfaMethod } from 'types';
import ValidationErrors from '../../../atoms/validation-errors';
import TotpCodeInput, { ITheme } from '../../../atoms/totp-code-input';
import MfaTotpIcon from '../../../atoms/mfa-totp-icon';
import MfaSmsIcon from '../../../atoms/mfa-sms-icon';
import { device } from '../../../../assets/styles/device';

const getHeadingText = (method: string | undefined): string => {
  switch (method) {
    case MfaMethod.TOTP:
      return 'Use an authenticator app';
    case MfaMethod.SMS:
      return 'Send a code to a phone number';
    default:
      return '';
  }
};

const getContentHeadingText = (method: string | undefined): string => {
  switch (method) {
    case MfaMethod.TOTP:
      return 'Enter the 6-digit code found in your authenticator app';
    case MfaMethod.SMS:
      return 'Enter the 6-digit code that has been sent to your mobile device';
    default:
      return '';
  }
};

const getIcon = (method: string | undefined): React.ReactNode => {
  switch (method) {
    case MfaMethod.TOTP:
      return <MfaTotpIcon />;
    case MfaMethod.SMS:
      return <MfaSmsIcon />;
    default:
      return null;
  }
};

export interface IMfaAuth {
  isMobile?: boolean;
  method?: string;
  uniLogo: string;
  serverErrors?: string[];
  onSubmit: (code: string) => void;
  onResendCode: () => void;
  onAuthHelp: () => void;
  onSignOut?: () => void;
}

const mobileTheme: ITheme = {
  margin: '0 4px',
  widthPx: 32,
  heightPx: 40,
  fontSize: 18
};

export default function MfaAuth({
  isMobile = false,
  method,
  uniLogo,
  serverErrors,
  onResendCode,
  onSubmit,
  onAuthHelp,
  onSignOut
}: IMfaAuth): ReactElement {
  const [code, setCode] = useState('');
  const headingText = getHeadingText(method);
  const contentHeading = getContentHeadingText(method);
  const icon = getIcon(method);

  const onCodeChange = (value: string) => {
    setCode(value);
  };

  const handleSubmit = () => {
    onSubmit(code);
  };

  const handleKeyPress = (event: { key: string }) => {
    if (event.key === 'Enter') {
      onSubmit(code);
    }
  };

  return (
    <MfaBackground>
      <MfaLayout>
        <MfaHeader>
          <UniLogo src={uniLogo} />
        </MfaHeader>

        <MfaContent>
          <Container>
            <ContainerHeader>
              {icon}
              <ContainerHeading data-selector="mfa-auth-heading">{headingText}</ContainerHeading>
            </ContainerHeader>

            <StyledContainerContent>
              <ContentHeading>{contentHeading}</ContentHeading>
              <div onKeyPress={handleKeyPress}>
                <TotpCodeInput onChange={onCodeChange} digits={6} value={code} theme={isMobile ? mobileTheme : {}} />
              </div>

              {serverErrors && <ValidationErrors messages={['The code is invalid or expired.']} />}

              <StyledButton data-selector="mfa-auth-confirm" onClick={handleSubmit}>
                Enter Code
              </StyledButton>

              {method !== MfaMethod.TOTP && <ResentCodeButton onClick={onResendCode}>Resend code</ResentCodeButton>}

              <StyledLink onClick={onAuthHelp}>Need help authenticating?</StyledLink>
            </StyledContainerContent>
          </Container>
          {onSignOut && <SignOutButton onClick={onSignOut}>Sign out</SignOutButton>}
        </MfaContent>
      </MfaLayout>
    </MfaBackground>
  );
}

const StyledContainerContent = styled(ContainerContent)`
  padding: 32px 58px 40px;
  @media ${device.mobile} {
    padding: 24px;
  }
`;

const ContentHeading = styled.div`
  margin-bottom: 32px;
  font-weight: 700;
  font-size: 18px;
  line-height: 18px;
  @media ${device.mobile} {
    margin-bottom: 16px;
  }
`;

const ResentCodeButton = styled.button`
  display: block;
  margin: 0 auto 20px;
  border: none;
  background: none;
  font-size: 18px;
  line-height: 18px;
  color: #00bcff;
  text-align: center;
  cursor: pointer;
`;
